function ecViewerSlideIndexFromScroll(slider, slideElements) {
  const slides = Array.from(slideElements);
  if (!slider || slides.length === 0) return 1;
  const n = slides.length;
  const maxScroll = slider.scrollWidth - slider.clientWidth;
  const sl = slider.scrollLeft;
  const eps = 2;
  if (maxScroll <= eps) {
    let idx = 0;
    for (let i = 0; i < n; i++) {
      if (slides[i].offsetLeft + slides[i].offsetWidth > sl + eps) {
        idx = i;
        break;
      }
      idx = i;
    }
    return idx + 1;
  }
  if (sl >= maxScroll - eps) return n;
  if (sl <= eps) return 1;
  const viewL = sl;
  const viewR = sl + slider.clientWidth;
  let best = 0;
  let bestVis = -1;
  for (let i = 0; i < n; i++) {
    const el = slides[i];
    const l = el.offsetLeft;
    const r = l + el.clientWidth;
    const vis = Math.max(0, Math.min(viewR, r) - Math.max(viewL, l));
    if (vis > bestVis) {
      bestVis = vis;
      best = i;
    }
  }
  return best + 1;
}

function ecViewerSlides(viewerSliderComponent) {
  const slider =
    viewerSliderComponent?.slider || viewerSliderComponent?.querySelector?.('[id^="Slider-"]');
  if (!slider) return [];
  return Array.from(slider.querySelectorAll(':scope > li.slider__slide'));
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          if (event.detail?.currentElement) {
            this.playActiveMedia(event.detail.currentElement);
          }
        });
        const gallery = this;
        const scrollbarTrack = this.querySelector('.ec-media-gallery-scrollbar-track');
        const scrollbarDrag = this.querySelector('.ec-media-gallery-scrollbar-drag');
        let scrollbarRaf = null;
        let isScrollbarDragging = false;

        const onSliderScroll = () => {
          if (scrollbarRaf) cancelAnimationFrame(scrollbarRaf);
          scrollbarRaf = requestAnimationFrame(() => {
            const slider = gallery.elements.viewer?.slider;
            if (!slider) return;

            const maxScroll = slider.scrollWidth - slider.clientWidth;
            const slides = ecViewerSlides(gallery.elements.viewer);
            const totalSlides = slides.length || 1;

            const currentSlide = ecViewerSlideIndexFromScroll(slider, slides);

            // 2. Update text counters
            const sliderCounter = gallery.elements.viewer.querySelector('.slider-counter--current');
            if (sliderCounter) sliderCounter.textContent = currentSlide;
            const thumbsCounter = gallery.querySelector('.thumbs-counter--current');
            if (thumbsCounter) thumbsCounter.textContent = currentSlide;

            // 3. Update scrollbar drag position and width continuously
            if (scrollbarDrag && scrollbarTrack && !isScrollbarDragging) {
              const trackWidth = scrollbarTrack.offsetWidth || scrollbarTrack.clientWidth;
              if (trackWidth > 0) {
                const dragWidth = trackWidth / totalSlides;
                scrollbarDrag.style.width = `${dragWidth}px`;

                const actualDragWidth = scrollbarDrag.offsetWidth || dragWidth;
                const maxLeft = trackWidth - actualDragWidth;
                let leftPosition = 0;
                if (maxScroll > 0) {
                  const scrollProgress = slider.scrollLeft / maxScroll;
                  leftPosition = scrollProgress * maxLeft;
                }
                scrollbarDrag.style.left = `${Math.max(0, Math.min(leftPosition, maxLeft))}px`;
                scrollbarDrag.style.display = 'block';
              }
            }

            scrollbarRaf = null;
          });
        };

        const setupViewerScrollListener = () => {
          const viewerSlider = gallery.elements.viewer?.slider;
          if (viewerSlider) {
            viewerSlider.addEventListener('scroll', onSliderScroll, { passive: true });
          }
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => setTimeout(setupViewerScrollListener, 100));
        } else {
          setTimeout(setupViewerScrollListener, 100);
        }
        
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        
        this.fixCustomVideoOrder();
        this.setupVerticalThumbnailNav();
        
        if (scrollbarTrack) {
          scrollbarTrack.addEventListener('click', (e) => {
            if (e.target.classList.contains('ec-media-gallery-scrollbar-drag')) return;
            const trackRect = scrollbarTrack.getBoundingClientRect();
            const clickX = e.clientX - trackRect.left;
            const trackWidth = trackRect.width;
            const slides = ecViewerSlides(this.elements.viewer);
            const totalSlides = slides.length || 1;
            const slideIndex = Math.min(Math.max(Math.ceil((clickX / trackWidth) * totalSlides), 1), totalSlides);
            
            if (slides[slideIndex - 1]) {
              const targetSlide = slides[slideIndex - 1];
              const mediaId = targetSlide.dataset.mediaId;
              
              if (this.elements.viewer.slider) {
                this.elements.viewer.slider.scrollTo({
                  left: targetSlide.offsetLeft,
                  behavior: 'smooth'
                });
              }
              
              this.setActiveMedia(mediaId, false);
            }
          });
        }
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        if (scrollbarDrag && scrollbarTrack) {
          const initScrollbar = () => {
            const slides = ecViewerSlides(gallery.elements.viewer);
            const activeSlide = slides.find((li) => li.classList.contains('is-active'));
            let currentIndex = 0;
            
            if (activeSlide) {
              currentIndex = slides.indexOf(activeSlide);
            } else if (slides.length > 0) {
              currentIndex = 0;
            }
            
            const currentPosition = (currentIndex + 1).toString();
            gallery.updateCounters(currentPosition);
          };
          
          const startScrollbarDrag = (e) => {
            e.preventDefault();
            isScrollbarDragging = true;
            const slider = gallery.elements.viewer?.slider;
            if (!slider || !scrollbarTrack) return;
            
            const handleMove = (moveEvent) => {
              const trackRect = scrollbarTrack.getBoundingClientRect();
              const clientX = moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX;
              const clickX = Math.max(0, Math.min(clientX - trackRect.left, trackRect.width));
              const trackWidth = trackRect.width;
              const slides = ecViewerSlides(gallery.elements.viewer);
              const totalSlides = slides.length || 1;
              const progress = Math.min(1, Math.max(0, clickX / trackWidth));
              const slideIndex = Math.min(Math.max(Math.floor(progress * totalSlides), 0), slides.length - 1);
              
              if (slides[slideIndex]) {
                slider.scrollTo({ left: slides[slideIndex].offsetLeft, behavior: 'auto' });
                gallery.setActiveMedia(slides[slideIndex].dataset.mediaId, false);
              }
            };
            
            const handleEnd = () => {
              isScrollbarDragging = false;
              document.removeEventListener('mousemove', handleMove);
              document.removeEventListener('mouseup', handleEnd);
              document.removeEventListener('touchmove', handleMove);
              document.removeEventListener('touchend', handleEnd);
            };
            
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleEnd);
            document.addEventListener('touchmove', handleMove);
            document.addEventListener('touchend', handleEnd);
          };
          
          scrollbarDrag.addEventListener('mousedown', startScrollbarDrag);
          scrollbarDrag.addEventListener('touchstart', startScrollbarDrag, { passive: false });
          
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
              window.setTimeout(initScrollbar, 200);
            });
          } else {
            window.setTimeout(initScrollbar, 200);
          }
          
          if (window.ResizeObserver) {
            const resizeObserver = new ResizeObserver(() => {
              initScrollbar();
            });
            resizeObserver.observe(scrollbarTrack);
          }
        }
      }

      setupVerticalThumbnailNav() {
        const sliderComponent = this.elements.thumbnails;
        if (!sliderComponent?.classList.contains('thumbnail-slider')) return;

        const thumbnailList = sliderComponent.querySelector('.thumbnail-list.slider--tablet-up');
        const prevButton = sliderComponent.querySelector('.slider-button--prev');
        const nextButton = sliderComponent.querySelector('.slider-button--next');

        if (!thumbnailList || !prevButton || !nextButton) return;
        if (sliderComponent.dataset.ecThumbNavInit === 'true') return;
        sliderComponent.dataset.ecThumbNavInit = 'true';

        const siblingThumbnailLi = (li, dir) => {
          let el = dir === 'next' ? li.nextElementSibling : li.previousElementSibling;
          while (el && !el.matches('li[data-target]')) {
            el = dir === 'next' ? el.nextElementSibling : el.previousElementSibling;
          }
          return el;
        };

        const getActiveThumbnailLi = () =>
          thumbnailList
            .querySelector('li[data-target] button[aria-current="true"]')
            ?.closest('li[data-target]') ||
          thumbnailList.querySelector('li[data-target] button[aria-current]')?.closest('li[data-target]');

        const updateButtonStates = () => {
          const activeItem = getActiveThumbnailLi();
          if (!activeItem) {
            prevButton.removeAttribute('disabled');
            nextButton.removeAttribute('disabled');
            return;
          }

          if (siblingThumbnailLi(activeItem, 'prev')) {
            prevButton.removeAttribute('disabled');
          } else {
            prevButton.setAttribute('disabled', 'disabled');
          }

          if (siblingThumbnailLi(activeItem, 'next')) {
            nextButton.removeAttribute('disabled');
          } else {
            nextButton.setAttribute('disabled', 'disabled');
          }
        };

        const selectAdjacentThumbnail = (direction) => {
          const activeItem = getActiveThumbnailLi();
          if (!activeItem) return;
          const targetItem =
            direction === 'next'
              ? siblingThumbnailLi(activeItem, 'next')
              : siblingThumbnailLi(activeItem, 'prev');
          if (!targetItem) return;
          const mediaId = targetItem.getAttribute('data-target');
          if (!mediaId) return;
          this.setActiveMedia(mediaId, false);
        };

        prevButton.addEventListener('click', (e) => {
          e.preventDefault();
          selectAdjacentThumbnail('prev');
        });

        nextButton.addEventListener('click', (e) => {
          e.preventDefault();
          selectAdjacentThumbnail('next');
        });

        this.updateThumbnailNavButtons = updateButtonStates;
        thumbnailList.addEventListener('scroll', updateButtonStates);
        updateButtonStates();
      }

      fixCustomVideoOrder() {
        if (!this.mql.matches) return;
        
        const viewer = this.elements.viewer;
        if (!viewer) return;
        
        const ul = viewer.querySelector('ul');
        if (!ul) return;
        
        const customVideoSlide = ul.querySelector(':scope > li.slider__slide[data-media-id*="custom-video"]');
        const featuredMediaSlide = ul.querySelector(':scope > li.slider__slide.is-active');
        
        if (!customVideoSlide || !featuredMediaSlide) return;
        
        const customVideoIndex = Array.from(ul.children).indexOf(customVideoSlide);
        const featuredMediaIndex = Array.from(ul.children).indexOf(featuredMediaSlide);
        
        if (customVideoIndex < featuredMediaIndex) {
          const nextSibling = featuredMediaSlide.nextElementSibling;
          if (nextSibling) {
            ul.insertBefore(customVideoSlide, nextSibling);
          } else {
            ul.appendChild(customVideoSlide);
          }
        }
        
        if (this.elements.thumbnails) {
          const thumbnailsUl = this.elements.thumbnails.querySelector('ul');
          if (thumbnailsUl && featuredMediaSlide) {
            const customVideoThumb = thumbnailsUl.querySelector('[data-target*="custom-video"]');
            const featuredMediaId = featuredMediaSlide.dataset.mediaId;
            const featuredMediaThumb = thumbnailsUl.querySelector(`[data-target="${featuredMediaId}"]`);
            
            if (customVideoThumb && featuredMediaThumb) {
              const customVideoThumbIndex = Array.from(thumbnailsUl.children).indexOf(customVideoThumb);
              const featuredMediaThumbIndex = Array.from(thumbnailsUl.children).indexOf(featuredMediaThumb);
              
              if (customVideoThumbIndex < featuredMediaThumbIndex) {
                const nextThumbSibling = featuredMediaThumb.nextElementSibling;
                if (nextThumbSibling) {
                  thumbnailsUl.insertBefore(customVideoThumb, nextThumbSibling);
                } else {
                  thumbnailsUl.appendChild(customVideoThumb);
                }
              }
            } else if (customVideoThumb && !featuredMediaThumb) {
              const firstThumb = thumbnailsUl.children[0];
              if (firstThumb && customVideoThumbIndex === 0 && thumbnailsUl.children.length > 1) {
                const secondThumb = thumbnailsUl.children[1];
                if (secondThumb) {
                  thumbnailsUl.insertBefore(customVideoThumb, secondThumb.nextElementSibling);
                }
              }
            }
          }
        }

        this.updateThumbnailNavButtons?.();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails ? this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        ) : null;
        
        if (thumbnail) {
          this.setActiveThumbnail(thumbnail);
          if (thumbnail.dataset.mediaPosition) {
            this.updateCounters(thumbnail.dataset.mediaPosition);
          }
        } else {
          const slides = ecViewerSlides(this.elements.viewer);
          const slideEl =
            event.detail.currentElement.closest?.('li.slider__slide') ?? event.detail.currentElement;
          const currentIndex = slides.indexOf(slideEl);
          if (currentIndex !== -1) {
            this.updateCounters((currentIndex + 1).toString());
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const slides = ecViewerSlides(this.elements.viewer);
        let activeMedia = this.elements.viewer.querySelector(`li.slider__slide[data-media-id="${mediaId}"]`);
        if (!activeMedia && mediaId) {
          activeMedia = this.elements.viewer
            .querySelector(`[data-media-id="${mediaId}"]`)
            ?.closest?.('li.slider__slide');
        }
        if (!activeMedia && slides.length) {
          activeMedia = slides[0];
        }
        if (!activeMedia) {
          return;
        }
        slides.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const prependThumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (!prependThumb) {
            } else if (prependThumb.parentElement.firstChild !== prependThumb) {
              prependThumb.parentElement.prepend(prependThumb);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (!activeThumbnail) {
          }
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail && activeThumbnail.dataset.mediaPosition) {
            this.updateCounters(activeThumbnail.dataset.mediaPosition);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        } else {
          const slides = ecViewerSlides(this.elements.viewer);
          const currentIndex = slides.indexOf(activeMedia);
          if (currentIndex !== -1) {
            this.updateCounters((currentIndex + 1).toString());
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails) {
          return;
        }
        if (!thumbnail) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const thumbBtn = thumbnail.querySelector('button');
        if (!thumbBtn) {
          return;
        }
        thumbBtn.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.slider) {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        } else {
          const thumbList = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (thumbList) {
            const listRect = thumbList.getBoundingClientRect();
            const thumbRect = thumbnail.getBoundingClientRect();
            const isVisible = (thumbRect.top >= listRect.top) && (thumbRect.bottom <= listRect.bottom);
            
            if (!isVisible) {
              const relativeTop = thumbnail.offsetTop - thumbList.offsetTop;
              const targetScrollTop = relativeTop - (thumbList.clientHeight / 2) + (thumbnail.clientHeight / 2);
              
              thumbList.scrollTo({
                top: Math.max(0, Math.min(targetScrollTop, thumbList.scrollHeight - thumbList.clientHeight)),
                behavior: 'smooth'
              });
            }
          }
        }

        this.updateThumbnailNavButtons?.();
      }

      updateCounters(position) {
        const sliderCounter = this.elements.viewer.querySelector('.slider-counter--current');
        const thumbsCounter = this.querySelector('.thumbs-counter--current');
        const scrollbarDrag = this.querySelector('.ec-media-gallery-scrollbar-drag');
        const scrollbarTrack = this.querySelector('.ec-media-gallery-scrollbar-track');
        
        if (sliderCounter) {
          sliderCounter.textContent = position;
        }
        if (thumbsCounter) {
          thumbsCounter.textContent = position;
        }
        
        if (scrollbarDrag && scrollbarTrack) {
          const slides = ecViewerSlides(this.elements.viewer);
          const totalSlides = slides.length || 1;
          const currentSlide = parseInt(position) || 1;
          
          if (totalSlides > 0 && currentSlide > 0) {
            const trackWidth = scrollbarTrack.offsetWidth || scrollbarTrack.clientWidth;
            
            if (trackWidth > 0) {
              const dragWidth = trackWidth / totalSlides;
              scrollbarDrag.style.width = `${dragWidth}px`;
              
              const actualDragWidth = scrollbarDrag.offsetWidth || dragWidth;
              const maxLeft = trackWidth - actualDragWidth;
              const leftPosition = totalSlides > 1 ? ((currentSlide - 1) / (totalSlides - 1)) * maxLeft : 0;
              
              scrollbarDrag.style.left = `${Math.max(0, Math.min(leftPosition, maxLeft))}px`;
              scrollbarDrag.style.display = 'block';
            }
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        this.resumePlaybackInSlide(activeItem);
        requestAnimationFrame(() => this.resumePlaybackInSlide(activeItem));
      }

      resumePlaybackInSlide(slide) {
        if (!slide) return;
        slide.querySelectorAll('video').forEach((video) => {
          video.play().catch(() => {});
        });
        slide.querySelectorAll('iframe.js-youtube').forEach((iframe) => {
          try {
            iframe.contentWindow?.postMessage(
              '{"event":"command","func":"playVideo","args":""}',
              '*'
            );
          } catch (_) {}
        });
        slide.querySelectorAll('iframe.js-vimeo').forEach((iframe) => {
          try {
            iframe.contentWindow?.postMessage('{"method":"play"}', '*');
          } catch (_) {}
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', function() {
  setTimeout(() => {
    const mediaGalleries = document.querySelectorAll('media-gallery');
    mediaGalleries.forEach(gallery => {
      if (gallery.fixCustomVideoOrder) {
        gallery.fixCustomVideoOrder();
      }
      if (gallery.setupVerticalThumbnailNav) {
        gallery.setupVerticalThumbnailNav();
      }
    });
  }, 100);
});
